import React, { useState } from 'react';
import { useHistory } from 'react-router';
import styled from 'styled-components';
import { formattedDate } from '@/utils/time';
import _ from 'lodash';

import Table from '@/components/Table';
import useOrderQuery from '@/hooks/order/useOrderQuery';
import { Order, PreparingOrdersParams } from '@/apis/order/interface';
import { formatToIconOrderType, orderTableStatus } from '@/utils/tableFormat';
import { formatPhoneDefault } from '@/utils/form';
import moment from 'moment';
import SearchHeader from './containers/SearchHeader';
import { ReactComponent as ImmediateOrderBadge } from '@/assets/images/order/immediateOrderBadge.svg';
import theme from '@/styles/theme';

const PreparingOrdersPage = () => {
  const history = useHistory();
  const {
    preparingOrdersQuery,
    getSearchProductsByPharmacistQuery,
    orderReceivedMutation
  } = useOrderQuery();
  const listType = 'preparing';
  const [body, setBody] = useState<PreparingOrdersParams>({
    page: 1,
    limit: 10,
    startDate: moment().subtract(7, 'd').format('YYYY-MM-DD'),
    endDate: moment().format('YYYY-MM-DD'),
    status: null,
    sort: null
  });
  const { data: orderData } = preparingOrdersQuery(body);
  const { data: additionalOrderProducts = [] } =
    getSearchProductsByPharmacistQuery();

  const isAdditionalOrderPharmacy = additionalOrderProducts.length > 0;

  //Table props
  const headers = [
    { name: '상태', flex: 1, alias: 'orderStatus' },
    { name: '수령방법', flex: 1, alias: 'orderType' },
    { name: '결제 완료시각', flex: 1.5, alias: 'paidAt' },
    { name: '성함', flex: 0.8, alias: 'username' },
    { name: '전화번호', flex: 1.5, alias: 'phone' },
    { name: '주소', flex: 4.7, alias: 'address', left: true },
    { name: '비고', flex: 1.5, alias: 'additionalStatus' }
  ];

  const rowDataFormat = (item: Order, alias: string) => {
    const roadAddress =
      item.orderType === 'delivery' && item.agencyType === 'barogo_today';
    switch (alias) {
      case 'orderType':
        return formatToIconOrderType(item.orderType, item.agencyType);
      case 'paidAt':
        return formattedDate(item.paidAt!, 'YYYY-MM-DD HH:mm');
      case 'username':
        return item.username;
      case 'phone':
        return formatPhoneDefault(item.phone);
      case 'address':
        return `${roadAddress ? item.roadAddress : item.jibunAddress}${
          item.detailAddress ? ', ' + item.detailAddress : ''
        }`;
      case 'orderStatus':
        return orderTableStatus(item);
      case 'additionalStatus':
        return (
          <Col>
            {item.isImmediatePayment && <ImmediateOrderBadge />}
            {item.isPrescriptionRevised && (
              <AdditionalStatusText textColor={theme.DF_COLOR.RED800}>
                처방전수정
              </AdditionalStatusText>
            )}
            {item.hasAdditionalOrder && (
              <AdditionalStatusText textColor={theme.DF_COLOR.YELLOW800}>
                추가 주문
              </AdditionalStatusText>
            )}
          </Col>
        );
    }
  };

  const goOrderDetailPage = (id: number, item?: Order) => {
    if (!_.isEmpty(item)) {
      if (!item?.orderId) {
        return;
      }
      if (!item?.checkedAt) {
        orderReceivedMutation.mutate(item?.orderId!);
      }
      history.push({ pathname: `/${listType}/${item?.orderId}` });
    }
  };

  const highLightCheck = (item?: Order) => {
    return item?.checkedAt === null;
  };

  return (
    <Wrapper>
      <SearchHeader
        body={body}
        setBody={setBody}
        isAdditionalOrderPharmacy={isAdditionalOrderPharmacy}
      />
      <Table
        data={orderData?.list || []}
        headers={headers}
        rowDataFormat={rowDataFormat}
        page={body.page}
        totalCount={orderData?.pageable.totalCount || 0}
        setPage={(page: number) => {
          setBody({ ...body, page });
        }}
        itemsCountPerPage={body.limit!}
        onClick={goOrderDetailPage}
        highLightCheck={highLightCheck}
      />
    </Wrapper>
  );
};

export default PreparingOrdersPage;

const Wrapper = styled.div`
  width: 100%;

  ${({ theme }) => theme.media.threeQuarter`
    width: 100%;
  `};
`;

const Col = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: column;
  svg + span {
    margin-top: 4px;
  }
`;

const AdditionalStatusText = styled.span<{ textColor: string }>`
  ${({ theme, textColor }) => `
    ${theme.DF_TYPOGRAPHY.UI15R};
    color: ${textColor};
  `};
`;
